/**
 * Glue between the live HUD (world + registry + queries) and the pure describe* models, so the
 * edits in actionBar/nameplates/unitFrame stay one-liners.
 */
import type { CastFailReason, ContentRegistry, DeepReadonly, SimQueries, UnitId, World } from '@keystone/sim';
import { casterFromUnit, describeAbility, describeAura, describeUnitCast } from './describe';
import { fmtSec } from './format';
import type { TooltipModel } from './types';

/** What the HUD knows each frame. */
export interface TooltipLive {
  world: DeepReadonly<World>;
  content: ContentRegistry;
  queries?: SimQueries | null;
  playerId: UnitId | null;
  targetId?: UnitId | null;
}

function safe<T>(f: () => T, fallback: T): T {
  try {
    return f();
  } catch {
    return fallback;
  }
}

function errorText(reason: CastFailReason, live: TooltipLive, abilityId: string): string | null {
  const a = live.content.ability(abilityId);
  switch (reason) {
    case 'locked': {
      // +field: ClassDef.unlocks gates abilities by hero level.
      const cls = live.playerId ? live.content.class(live.world.units[live.playerId]?.classId ?? '') : undefined;
      const lvl = cls?.unlocks?.[abilityId];
      return lvl ? `Unlocks at level ${lvl}` : 'Not learned yet';
    }
    case 'no_resource': {
      const cls = live.playerId ? live.content.class(live.world.units[live.playerId]?.classId ?? '') : undefined;
      const res = a?.cost?.[0]?.resource;
      const name = cls?.resources.find((r) => r.id === res)?.name ?? 'resource';
      return `Not enough ${name}`;
    }
    case 'out_of_range':
      return 'Out of range';
    case 'not_facing':
      return 'You must be facing your target';
    case 'condition_failed':
      return 'Requirements not met';
    case 'silenced':
      return 'Silenced';
    case 'school_locked':
      return 'That school is locked out';
    case 'cannot_act':
      return 'You cannot do that right now';
    case 'rooted':
      return 'You can’t move right now';
    case 'invalid_target':
      return 'Invalid target';
    case 'dead':
      return 'You are dead';
    default:
      return null;
  }
}

/** Transform offers per base ability id (rotation rework F2), built once per registry. */
const OFFERS = new WeakMap<ContentRegistry, Map<string, { auraName: string; formId: string }[]>>();
function transformOffers(content: ContentRegistry, baseId: string): readonly { auraName: string; formId: string }[] {
  let m = OFFERS.get(content);
  if (!m) {
    m = new Map();
    OFFERS.set(content, m);
    for (const def of content.auras?.values() ?? []) {
      for (const o of def.overrides ?? []) {
        const list = m.get(o.abilityId) ?? [];
        if (!list.some((x) => x.formId === o.with)) list.push({ auraName: def.name, formId: o.with });
        m.set(o.abilityId, list);
      }
    }
  }
  return m.get(baseId) ?? [];
}

/**
 * Action-bar slot: the player's (compiled) ability with live stats, keybind and red usability errors.
 * `baseId` is the bound id when the slot currently shows a transformed form (`abilityId`).
 */
export function actionTooltipModel(live: TooltipLive | null, abilityId: string | null | undefined, keybind: string, baseId?: string | null): TooltipModel | null {
  if (!live || !abilityId || !live.playerId) return null;
  const { world, content, queries, playerId } = live;
  const a = content.ability(abilityId);
  const u = world.units[playerId];
  if (!a || !u) return null;
  const bound = baseId ?? abilityId;
  const offers = transformOffers(content, bound);
  let from: { name: string; via: string } | null = null;
  if (bound !== abilityId) {
    const offer = offers.find((o) => o.formId === abilityId);
    from = { name: content.ability(bound)?.name ?? bound, via: offer?.auraName ?? 'its proc' };
  }
  const into = bound === abilityId ? offers.map((o) => ({ name: content.ability(o.formId)?.name ?? o.formId, via: o.auraName })) : [];
  const eff = queries ? safe(() => queries.effectiveStats(playerId), null) : null;
  const cls = u.classId ? content.class(u.classId) : undefined;
  const errors: string[] = [];
  if (queries) {
    const target = a.targeting === 'ground' || a.targeting === 'self' || a.targeting === 'none' ? null : (live.targetId ?? null);
    // The sim resolves the override from the bound id.
    const r = safe(() => queries.canCast(playerId, bound, target, null), null);
    if (r && !r.ok) {
      const t = errorText(r.reason, live, a.id);
      if (t) errors.push(t);
    }
    const cd = safe(() => queries.cooldownRemaining(playerId, a.id), 0);
    if (cd > 0) errors.push(`Cooldown remaining: ${fmtSec(cd)} sec`);
  }
  return describeAbility(a, casterFromUnit(u, eff), content, { keybind, classDef: cls ?? null, errors, transform: { from, into } });
}

/** Cast bar hover: what the unit is casting (enemy casts show interruptibility and effects). */
export function castTooltipModel(live: TooltipLive | null, unitId: UnitId | null | undefined): TooltipModel | null {
  if (!live || !unitId) return null;
  const u = live.world.units[unitId];
  return describeUnitCast(u, live.world, live.content);
}

/** Aura icon hover on a unit (by aura instance id). */
export function auraTooltipModel(live: TooltipLive | null, unitId: UnitId | null | undefined, iid: string | number | null | undefined): TooltipModel | null {
  if (!live || !unitId || iid === null || iid === undefined) return null;
  const u = live.world.units[unitId];
  const inst = u?.auras.find((a) => String(a.iid) === String(iid));
  if (!u || !inst) return null;
  const caster = live.world.units[inst.casterId];
  return describeAura(inst, live.content.aura(inst.auraId), {
    content: live.content,
    now: live.world.time,
    casterName: caster?.name ?? null,
    caster: caster ? casterFromUnit(caster) : null,
    enemyCaster: caster?.faction === 'enemy',
  });
}
